import React, { useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Chip,
  Paper
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const LogoPreview = ({ logo, color, backgroundColor, canvasRef, originalImageData }) => {
  const imageRef = useRef(new Image());

  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : { r: 0, g: 0, b: 0 };
  };

  const applyColorToImageData = (imageData, targetColor) => {
    const data = imageData.data;
    const rgb = hexToRgb(targetColor);
    
    for (let i = 0; i < data.length; i += 4) {
      const alpha = data[i + 3];
      
      if (alpha > 0) {
        const brightness = (data[i] + data[i + 1] + data[i + 2]) / 3;
        const factor = brightness / 128;
        
        data[i] = Math.min(255, rgb.r * factor);
        data[i + 1] = Math.min(255, rgb.g * factor);
        data[i + 2] = Math.min(255, rgb.b * factor);
      }
    }
    
    return imageData;
  };

  useEffect(() => {
    if (!originalImageData) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const img = imageRef.current;

    img.onload = () => {
      canvas.width = originalImageData.width;
      canvas.height = originalImageData.height;

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = backgroundColor;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      const coloredImageData = new ImageData(
        new Uint8ClampedArray(originalImageData.data.data),
        originalImageData.width,
        originalImageData.height
      );

      const finalImageData = applyColorToImageData(coloredImageData, color);
      ctx.putImageData(finalImageData, 0, 0);
    };

    img.src = logo;
  }, [logo, color, backgroundColor, originalImageData, canvasRef]);

  return (
    <Box>
      <Typography variant="h6" gutterBottom textAlign="center">
        Logo Preview
      </Typography>
      
      <Paper 
        variant="outlined" 
        sx={{ 
          p: 3, 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center',
          backgroundColor,
          minHeight: 400
        }}
      >
        <canvas
          ref={canvasRef}
          style={{ 
            maxWidth: '100%',
            maxHeight: 300,
            display: 'block'
          }}
        />
      </Paper>

      <Box mt={2} display="flex" gap={1} flexWrap="wrap" justifyContent="center">
        <Chip 
          icon={<CheckCircleIcon />} 
          label={`Logo Color: ${color}`}
          variant="outlined"
          sx={{ 
            backgroundColor: color,
            color: '#fff',
            fontWeight: 'bold'
          }}
        />
        <Chip 
          label={`Background: ${backgroundColor}`}
          variant="outlined"
          sx={{ 
            backgroundColor: backgroundColor,
            color: backgroundColor === '#ffffff' ? '#000' : '#fff',
            borderColor: 'divider'
          }}
        />
        {originalImageData && (
          <Chip 
            label={`Size: ${originalImageData.width}×${originalImageData.height}`}
            variant="outlined"
          />
        )}
      </Box>
    </Box>
  );
};

export default LogoPreview;